import { useState } from "react";
import type { FormEvent } from "react";
import type { EnrichedTag } from "../../shared/types.ts";
import { apiPost } from "../utils/api.ts";
import { Button } from "./Button.tsx";

interface AddTagProps {
  onClose: () => void;
  onTagAdded: (tag: EnrichedTag) => void;
}

export function AddTag({ onClose, onTagAdded }: AddTagProps) {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiPost("/api/tags", { value: value.trim() });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to add tag");
      }

      const data = await response.json();
      onTagAdded(data.tag);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 fade-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">Create Tag</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="tag-value"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Tag Name
            </label>
            <input
              type="text"
              id="tag-value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g., work, personal, reading"
              maxLength={64}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-coral focus:border-transparent outline-none transition"
              disabled={loading}
              autoFocus
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {value.length}/64 characters
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              disabled={!value.trim()}
              fullWidth
            >
              {loading ? "Creating..." : "Create Tag"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
